// app/src/lib/crash/index.js — anonymous PWA crash reporter.
//
// Exposed as `window.kanjo.crash`. Captures uncaught exceptions, unhandled
// promise rejections, resource-load failures, and React render crashes (via
// the CrashBoundary in main.jsx) and beacons a scrubbed, identifier-free
// report to POST /v1/crash.
//
// Privacy by construction:
//   - NO anon id, NO Cognito sub, NO cookies — reports are unlinkable to a
//     person (unlike funnel events, which deliberately carry the anon id).
//   - Every field comes from a fixed schema; the one freeform field (the error
//     message) goes through redact.js before it leaves the device, and the
//     Lambda scrubs it again server-side.
//   - Stack frames are basename:line:col of the minified bundles — no user
//     data can appear in them. scripts/crash-symbolicate.mjs maps them back to
//     source locations using the local (never uploaded) sourcemaps.
//
// Capture topology: app/crash-guard.js is loaded first in index.html and owns
// the window listeners from the first byte of JS, buffering raw events into
// window.__kanjoCrashQ. install() (called from main.jsx) takes over via
// window.__kanjoCrash and drains that buffer, so nothing between page load and
// lib boot is lost and no event is ever reported twice. If the guard is absent
// (e.g. an embed without the tag) install() wires its own listeners.
//
// Flood control: at most MAX_PER_SESSION reports per page load and
// MAX_PER_DAY distinct fingerprints per day per device (idbKv-persisted), so a
// crash loop reports once instead of thousands of times. Offline reports queue
// in idbKv and flush on the next launch / 'online' event.
//
// Fire-and-forget: every path is wrapped — the crash reporter itself must
// never throw into the app.

import { getConfig } from '../config.js';
import * as idbKv from '../idbKv.js';
import { redactMessage, parseStack, fingerprint } from './redact.js';

const KEY_SENT = 'crashSentLog';   // { day: 'YYYY-MM-DD', fps: string[] }
const KEY_QUEUE = 'crashQueue';    // pending report bodies (offline / beacon refused)
const MAX_PER_SESSION = 5;
const MAX_PER_DAY = 10;
const MAX_QUEUE = 20;

let sessionCount = 0;
let buildIdCache = '';

/** Per-deploy build id, read from the ?v= cache-buster build-prod.mjs stamps
 *  on every local script tag. Dev (no ?v=) reports as 'dev'. */
function buildId() {
  if (buildIdCache) return buildIdCache;
  try {
    const s = document.querySelector('script[src*="kanjo-lib.js"]');
    buildIdCache = s?.src.match(/[?&]v=([\w-]+)/)?.[1] || 'dev';
  } catch { buildIdCache = 'dev'; }
  return buildIdCache;
}

/** Current tile name from the hash route — a fixed app-defined string, never
 *  user data ("today", "reports", …). */
function routeTile() {
  try { return (location.hash.replace(/^#\/?/, '').split(/[/?]/)[0] || '').slice(0, 24); }
  catch { return ''; }
}

function normalize(err) {
  if (err instanceof Error) return { name: err.name || 'Error', message: err.message, stack: err.stack };
  if (typeof err === 'string') return { name: 'Error', message: err, stack: '' };
  try { return { name: err?.name || 'Error', message: err?.message ?? String(err), stack: err?.stack || '' }; }
  catch { return { name: 'Error', message: '', stack: '' }; }
}

function today() { return new Date().toISOString().slice(0, 10); }

/** Daily per-device dedup: send each fingerprint at most once per day, at most
 *  MAX_PER_DAY distinct ones. */
async function shouldSend(fp) {
  try {
    let log = await idbKv.get(KEY_SENT);
    if (!log || log.day !== today()) log = { day: today(), fps: [] };
    if (log.fps.includes(fp) || log.fps.length >= MAX_PER_DAY) return false;
    log.fps.push(fp);
    await idbKv.set(KEY_SENT, log);
    return true;
  } catch { return true; }   // idbKv broken → still report (session cap holds)
}

function send(body) {
  let base = '';
  try { base = getConfig().apiBaseUrl; } catch { /* not configured yet */ }
  if (!base || base.includes('invalid')) return false;
  const url = base.replace(/\/$/, '') + '/v1/crash';
  const json = JSON.stringify(body);
  try {
    // text/plain is CORS-safelisted → no preflight; the Lambda JSON.parses it.
    if (navigator.sendBeacon) return navigator.sendBeacon(url, new Blob([json], { type: 'text/plain' }));
    fetch(url, { method: 'POST', headers: { 'content-type': 'text/plain' }, body: json, keepalive: true, mode: 'cors' });
    return true;
  } catch { return false; }
}

async function enqueue(body) {
  try {
    const q = (await idbKv.get(KEY_QUEUE)) || [];
    q.push(body);
    await idbKv.set(KEY_QUEUE, q.slice(-MAX_QUEUE));
  } catch { /* drop */ }
}

/** Flush queued (offline) reports. Clears the queue first so a concurrent
 *  flush can't double-send; failures re-queue. */
export async function flush() {
  try {
    const q = (await idbKv.get(KEY_QUEUE)) || [];
    if (!q.length) return;
    await idbKv.set(KEY_QUEUE, []);
    for (const body of q) { if (!send(body)) await enqueue(body); }
  } catch { /* non-fatal */ }
}

/**
 * Report an error. Safe to call with anything (Error, string, rejection
 * reason); never throws.
 * @param {any} err
 * @param {{type?: 'error'|'unhandledrejection'|'react'|'resource'}} [ctx]
 */
export async function report(err, ctx = {}) {
  try {
    if (sessionCount >= MAX_PER_SESSION) return;
    const e = normalize(err);
    const frames = parseStack(e.stack);
    const b = buildId();
    const fp = fingerprint({ build: b, name: e.name, topFrame: frames[0] });
    if (!(await shouldSend(fp))) return;
    sessionCount++;
    const body = {
      v: 1,
      t: ctx.type || 'error',
      b,
      n: String(e.name || 'Error').slice(0, 60),
      m: redactMessage(e.message),
      s: frames,
      r: routeTile(),
    };
    if (navigator.onLine === false || !send(body)) await enqueue(body);
  } catch { /* the crash reporter must never crash the app */ }
}

/** Convert a raw crash-guard buffer entry into a report. */
export function handleRaw(entry) {
  try {
    if (!entry) return;
    if (entry.t === 'resource') {
      // Only the tag + script/style basename — self/unpkg/plaid/finix assets,
      // never user data (receipt images are blob:/data:, which don't error here).
      const base = String(entry.u || '').split('?')[0].split('/').pop() || '';
      report({ name: 'ResourceError', message: `${entry.tag || ''} ${base}`.trim(), stack: '' }, { type: 'resource' });
      return;
    }
    if (entry.t === 'unhandledrejection') { report(entry.e, { type: 'unhandledrejection' }); return; }
    const e = entry.e || { name: 'Error', message: entry.m || '', stack: `${entry.f || ''}:${entry.l || 0}:${entry.c || 0}` };
    report(e, { type: 'error' });
  } catch { /* non-fatal */ }
}

/**
 * Install the reporter: take over from crash-guard.js and drain its buffer.
 * Idempotent; called once from main.jsx at boot.
 */
export function install() {
  try {
    if (window.__kanjoCrash) return;
    window.__kanjoCrash = { report, handleRaw };
    const q = window.__kanjoCrashQ;
    if (Array.isArray(q)) {
      q.splice(0).forEach(handleRaw);
    } else {
      // No guard on this page — own the listeners directly. Capture phase so
      // resource-load errors (which don't bubble) are seen too.
      window.addEventListener('error', (ev) => {
        const tgt = ev.target;
        if (tgt && tgt !== window && tgt.tagName) {
          handleRaw({ t: 'resource', u: tgt.src || tgt.href || '', tag: tgt.tagName });
        } else {
          handleRaw({ t: 'error', e: ev.error, m: ev.message, f: ev.filename, l: ev.lineno, c: ev.colno });
        }
      }, true);
      window.addEventListener('unhandledrejection', (ev) => handleRaw({ t: 'unhandledrejection', e: ev.reason }));
    }
    window.addEventListener('online', () => { flush(); });
    flush();
  } catch { /* non-fatal */ }
}
